// Manufacturing + Production mode help bodies. Extracted so
// modeHelpRegistry.tsx stays under the file-size hard cap.
import type { ReactNode } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";

/** Returns help body for mfg/production modes, or null if not handled. */
export function getMfgHelpBody(mode: PrimaryMode): ReactNode | null {
  switch (mode) {
    case "mfgBuildPipeline":
      return (
        <div className="space-y-2">
          <p>
            Plant <strong>build pipeline</strong>: Plant Kanban (phase columns),{" "}
            <strong>Units</strong> roster, and phase settings.
            Moving a unit out of the initial phase issues a plant-prefixed
            serial; moving into build-complete records the COGS production-dollar event.
            ShedSuite customer orders land in <strong>Order received</strong> for Ops to
            approve, assign to a production load (A–ZZ), and send to the plant.
            When a unit is in <strong>Build complete</strong>, <strong>Ship from plant</strong>
            takes it off the board without waiting on a delivery route.
            <strong>Inventory request</strong> (far right of the stats row) lets the plant
            ask for materials without leaving the board.
          </p>
          <p>
            Units are no longer a separate Manufacturing mode — use the Units tab here.
          </p>
        </div>
      );
    case "mfgLaborBilling":
    case "mfgInventory":
    case "mfgModels":
    case "mfgPricing":
    case "mfgOverhead":
    case "mfgOrders":
    case "mfgPlants":
    case "mfgReports":
    case "mfgAdminHome":
      return (
        <div className="space-y-2">
          <p>
            Manufacturing add-on: inventory, receive lines, models, orders, plants,
            build pipeline (Kanban + units roster), and <strong>Labor rates</strong>{" "}
            (contractor piece-rate sandbox — bays + catalog + bay links; order sheets
            later). Plants holds the dealer network in a two-pane view.
          </p>
          <p>
            On the Inventory screen use the top <em>Stock / Count / Missing</em>{" "}
            control. <strong>Missing</strong> is the org inbox for unmatched
            material placeholders (one per usage) from models, variants, and part
            uses — amber when items are waiting. On Stock, the left column tabs
            through <em>Materials / Recipes / Changelog</em>; the right column
            tabs through <em>Receive lines / Recipe count</em>. Purchase orders
            and invoice receipts live in Production → <strong>Purchasing</strong>.
            Period totals from issued POs still roll up in Operations →{" "}
            <strong>Books</strong>. The Changelog is the per-event timeline
            (material receipts and recipe counts, plus adjustments and opening
            balances).
          </p>
          <p>
            Per-feature help lives inline next to the affordance — look for the
            small <strong>?</strong> button by the attention banner, the per-row
            status chips, the Review modal's vendor / shipping / tax inputs, and
            the Dismiss-alert control. Click any of those to see what that
            specific feature does without re-opening this overview.
          </p>
          <p>
            Access is for users with the org <strong>Manufacturing</strong> role
            (assigned by platform owner/admin) or internal{" "}
            <strong>platform</strong> support roles.
          </p>
        </div>
      );
    case "mfgFulfill":
      return (
        <p>
          <strong>Production</strong> bucket scaffold — the future home of the build pipeline kanban,
          station-level build tracking, materials consumption events (per-station as-built),
          labor time tracking, and throughput dashboards. Production was split out of the
          Manufacturing bucket as the floor / efficiency view; same role gate as Manufacturing
          for v1. Currently empty pending the first Production-bucket feature slice; see{" "}
          <code>docs/design/buckets_and_entitlements.md</code> §2 for the planned shape.
        </p>
      );
    case "mfgAudits":
      return (
        <div className="space-y-2">
          <p>
            <strong>Audits</strong> — Production home for BOM parts checks and Time Study labor
            capture. Use the BOM / Time Study slider (locked while a session is active).
          </p>
          <p>
            BOM kinds: <em>Audit a parts list for accuracy</em> or{" "}
            <em>Audit an active build for correct parts usage</em>. Distinct from desk{" "}
            <code>needs_review</code> / pending lines. DDL:{" "}
            <code>docs/sql/mfg_bom_audit_v1.sql</code>; Time Study:{" "}
            <code>docs/planning/mfg_time_study_research.md</code>.
          </p>
        </div>
      );
    case "mfgInventoryCount":
      return (
        <div className="space-y-2">
          <p>
            <strong>Inventory count</strong> — Production owns the plant materials count /
            reconcile session (open → count lines → close/post) and recipe count posting.
            Manufacturing → Inventory remains the desk for catalog, on-hand, and receive.
          </p>
          <p>
            Reuses MIC APIs. See <code>docs/planning/mfg_materials_inventory_count.md</code>.
            Dictate qty + name to match catalog rows. Exit without Submit keeps
            the plant session so you can resume, start a new snapshot (archives
            the draft), or discard it. Submit is the only path that posts
            on-hand adjustments.
          </p>
        </div>
      );
    case "mfgPurchasing":
      return (
        <div className="space-y-2">
          <p>
            <strong>Purchasing Assistant</strong> — Production home for purchase orders,
            vendor invoice receipts, and delivery confirmation. Issued PO qty is
            on-order (uncommitted) until an invoice is recorded and the plant
            confirms what landed, including off-quality chargebacks.
          </p>
          <p>
            Plant leads use <strong>Need materials</strong> to dump a shortage
            onto an incomplete PO. Purchasing gets a Firefly + banner to review,
            acknowledge, then issue (split vendors by hand if needed). The plant
            sees submitted → acknowledged → ordered with an expected timeframe.
          </p>
          <p>
            Inventory desk keeps catalog / on-hand / receive-lines. Add a vendor
            from the PO vendor dropdown (+ New) or the Vendors tab here.
          </p>
          <p>
            <strong>Packed audit</strong> corrects on-hand for the materials you
            look at in storage. It does not close a plant inventory. Outlook&apos;s
            next load buys from the corrected balance. Dictate packed and bay
            quantities to fill the form; Confirm still posts.
          </p>
        </div>
      );
    case "mfgWasteAssistant":
      return (
        <div className="space-y-2">
          <p>
            <strong>Waste Assistant</strong> records what is sitting in waste
            areas: how much to send back to the floor, and how much is true waste.
            Each new walk asks you to mark and remove scrap the first time, so a
            later walk does not count the same pile again.
          </p>
          <p>
            The trajectory is an early signal beside the period-close waste number.
            It does not replace a scrap event or a full inventory count. Dictate
            what you see and what to use to fill the form; Confirm still posts.
          </p>
        </div>
      );
    case "mfgLaborPay":
      return (
        <p>
          <strong>Labor pay</strong> is the company rate catalog and the serialized
          invoices. Manufacturing and ops add and edit rates ($ × qty), resolve
          unmatched order lines, and invoice. Plant leads look up those rates and
          stamp Labor OK. They cannot change the catalog.
        </p>
      );
    case "mfgQcDashboard":
      return (
        <div className="space-y-2">
          <p>
            <strong>QC Dashboard</strong> lists units waiting on inspection, each failure, and submitted
            reports that still have an uncorrected defect. Reopen jumps back to Builder Assistant.
          </p>
          <p>
            A serial typed in at report time with no build-pipeline stage shows as QC Special.
            Size is taken from the model text when it looks like 12x20.
          </p>
        </div>
      );
    case "mfgBuilderAssistant":
      return (
        <div className="space-y-2">
          <p>
            <strong>Builder Assistant</strong> captures what you see on a building.
            Type the serial and model you have. If inventory has no match, start
            anyway and link the unit later. QC findings and build-book examples
            are the same observation, tagged differently. Dictate or type, then
            add a photo.
          </p>
          <p>
            Submitting an inspection does not move the build pipeline and does not
            release labor pay. Marking a candidate reviewed is not an approved standard.
          </p>
        </div>
      );
    default:
      return null;
  }
}
